import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-hot-toast'
import {
  CogIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  getProtrackCredentials,
  upsertProtrackCredentials,
  verifyProtrackCredentials,
  deleteProtrackCredentials,
  type ProtrackCredentials,
} from '@/api/protrackCredentialsApi'

// Validation schema
const credentialsSchema = z.object({
  account: z.string().min(1, 'Protrack account is required'),
  password: z.string().min(1, 'Protrack password is required'),
  notes: z.string().optional(),
})

type CredentialsFormData = z.infer<typeof credentialsSchema>

export default function ProtrackCredentialsPage() {
  const [credentials, setCredentials] = useState<ProtrackCredentials | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const form = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      account: '',
      password: '',
      notes: '',
    },
  })

  // Fetch existing credentials on mount
  useEffect(() => {
    fetchCredentials()
  }, [])

  const fetchCredentials = async () => {
    try {
      setIsLoading(true)
      const data = await getProtrackCredentials()
      setCredentials(data)
      if (data) {
        form.reset({
          account: '', // Don't pre-fill for security
          password: '',
          notes: data.notes || '',
        })
      }
    } catch (error: any) {
      console.error('Error fetching credentials:', error)
      toast.error(error.response?.data?.message || 'Failed to load credentials')
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: CredentialsFormData) => {
    try {
      setIsSaving(true)
      const result = await upsertProtrackCredentials(data)
      setCredentials(result)
      toast.success(credentials ? 'Credentials updated successfully' : 'Credentials saved successfully')
      form.reset({
        account: '',
        password: '',
        notes: result.notes || '',
      })
    } catch (error: any) {
      console.error('Error saving credentials:', error)
      toast.error(error.response?.data?.message || 'Failed to save credentials')
    } finally {
      setIsSaving(false)
    }
  }

  const handleVerify = async () => {
    try {
      setIsVerifying(true)
      const result = await verifyProtrackCredentials()
      setCredentials(result)
      toast.success('Credentials verified and activated successfully!')
    } catch (error: any) {
      console.error('Error verifying credentials:', error)
      toast.error(error.response?.data?.message || 'Failed to verify credentials')
      // Refresh to get updated status
      fetchCredentials()
    } finally {
      setIsVerifying(false)
    }
  }

  const handleDelete = async () => {
    try {
      setIsDeleting(true)
      await deleteProtrackCredentials()
      setCredentials(null)
      setShowDeleteConfirm(false)
      form.reset({
        account: '',
        password: '',
        notes: '',
      })
      toast.success('Credentials deleted successfully')
    } catch (error: any) {
      console.error('Error deleting credentials:', error)
      toast.error(error.response?.data?.message || 'Failed to delete credentials')
    } finally {
      setIsDeleting(false)
    }
  }

  const getStatusBadge = () => {
    if (!credentials) return null

    const statusConfig = {
      ACTIVE: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        label: 'Active',
      },
      INACTIVE: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ExclamationCircleIcon,
        label: 'Inactive - Verification Required',
      },
      ERROR: {
        color: 'bg-red-100 text-red-800',
        icon: ExclamationCircleIcon,
        label: 'Error',
      },
      EXPIRED: {
        color: 'bg-gray-100 text-gray-800',
        icon: ExclamationCircleIcon,
        label: 'Expired',
      },
    }

    const config = statusConfig[credentials.status]
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CogIcon className="h-6 w-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">GPS Tracking Credentials</h1>
        </div>
        <p className="text-gray-600">
          Configure your Protrack GPS API credentials to enable vehicle tracking for your institution.
        </p>
      </div>

      {/* Current Status Card */}
      {credentials && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Current Configuration</h2>
            {getStatusBadge()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Status:</span>
              <span className="ml-2 font-medium text-gray-900">
                {credentials.isActive ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {credentials.lastVerifiedAt && (
              <div>
                <span className="text-gray-500">Last Verified:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {new Date(credentials.lastVerifiedAt).toLocaleString()}
                </span>
              </div>
            )}
            {credentials.lastErrorMessage && (
              <div className="md:col-span-2">
                <span className="text-gray-500">Last Error:</span>
                <span className="ml-2 font-medium text-red-600">{credentials.lastErrorMessage}</span>
              </div>
            )}
            {credentials.createdBy && (
              <div>
                <span className="text-gray-500">Created By:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {credentials.createdBy.firstName} {credentials.createdBy.lastName}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Last Updated:</span>
              <span className="ml-2 font-medium text-gray-900">
                {new Date(credentials.updatedAt).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Action Buttons for existing credentials */}
          <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="primary"
              onClick={handleVerify}
              loading={isVerifying}
              disabled={isVerifying}
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Verify Connection
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Remove Credentials
            </Button>
          </div>
        </div>
      )}

      {/* Credentials Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            {credentials ? 'Update Credentials' : 'Add Protrack Credentials'}
          </h2>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Protrack Account"
              placeholder="Enter your Protrack account"
              {...form.register('account')}
              error={form.formState.errors.account?.message}
            />

            <div className="relative">
              <Input
                label="Protrack Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your Protrack password"
                {...form.register('password')}
                error={form.formState.errors.password?.message}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea
              {...form.register('notes')}
              rows={3}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any notes about this configuration..."
            />
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">How it works:</h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Enter your Protrack GPS platform credentials</li>
              <li>Click "Save Credentials" to store them securely</li>
              <li>Click "Verify Connection" to test the connection with Protrack</li>
              <li>Once verified, GPS tracking features will be available for your institution</li>
            </ul>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={() => form.reset()}>
              Reset
            </Button>
            <Button type="submit" variant="primary" loading={isSaving}>
              {credentials ? 'Update Credentials' : 'Save Credentials'}
            </Button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Credentials</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your Protrack credentials? This will disable GPS tracking
              features for your institution until new credentials are configured.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
                Delete Credentials
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
